import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { styled } from '@kufox/mui/emotion';
import Dialog from '@kufox/mui/Dialog';
import Button from '@kufox/mui/Button';
import useTheme from '@kufox/mui/hooks/useTheme';

import { Trans, useTranslation } from '@tools/i18n';
import _ from 'lodash';
import NotReceiveDialog from './NotReceiveDialog';
import { getVoiceSupportCountry } from './service';
import { useLang } from '../../hookTool';
import { kcsensorsClick, kcsensorsManualTrack } from '../../common/tools';

const Container = styled.div`
  margin: 40px 0 16px;
  display: inline-block;
`;

const BaseText = styled.span(
  (props) => ({
    fontSize: props.theme.fonts.size.lg.fontSize,
    lineHeight: '24px',
    textDecoration: 'underline',
    color: props.theme.colors.primary,
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
    },
  }),
  (props) => {
    if (props.disable) {
      return {
        color: props.theme.colors.text20,
        cursor: 'default',
        '&:hover': {
          opacity: 1,
        },
      };
    }
  },
);

const PlaceElement = styled.span`
  visibility: hidden;
`;

const VoiceDialog = styled(Dialog)`
  zindex: '99999999 !important'; // 为了覆盖otc页面特别夸张的弹窗z-index值
`;

const SendBtnContainer = styled.div`
  margin-top: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const VoiceOkBtn = styled(Button)`
  flex: 1;
  margin-right: 12px;
`;

const VoiceCancelBtn = styled(Button)`
  flex: 1;
`;

let timer = null;

const voiceCode = (props) => {
  const {
    phone = '',
    email = '',
    countryCode = '',
    disable = false,
    loading = false,
    countTime = null,
    onTimeOver = () => {},
    validateFunc = undefined,
    onSend = () => {},
    trackingConfig = {},
  } = props || {};

  const { t } = useLang();
  const theme = useTheme();
  const { t: _t } = useTranslation('entrance');

  const [show, setShow] = useState(false);
  const [codes, setCodes] = useState([]); // 支持语音的区号列表
  const [clicked, setClicked] = useState(false); // 是否点击过
  const [visible, setVisible] = useState(false);
  const [countTimer, setCountTimer] = useState(0);
  const isSupportVoice =
    codes && codes.length && (countryCode ? codes.includes(countryCode) : true);

  useEffect(() => {
    // 获取支持语音的区号列表
    getVoiceSupportCountry().then((res) => {
      const { success = false, data = [] } = res || {};
      if (success && data && data.length) {
        const _list = [];
        data.forEach((item) => {
          const { mobileCode = '' } = item || {};
          if (mobileCode) {
            _list.push(mobileCode);
          }
        });
        setCodes(_list);
      }
    });
  }, []);

  const startCount = useCallback(
    (seconds) => {
      setCountTimer(seconds);
      if (seconds >= 1) {
        timer = _.delay(() => {
          startCount(seconds - 1);
        }, 1000);
      } else {
        clearTimeout(timer);
        onTimeOver && onTimeOver();
      }
    },
    [onTimeOver],
  );

  const _phone = useMemo(() => {
    const str = String(phone) || '';
    const len = str.length;
    if (!len) return str;
    if (len >= 4) {
      return ['***', str.substr(-4)].join('');
    }
    return ['***', str].join('');
  }, [phone]);

  const text = useMemo(() => {
    return clicked ? t('voice.title') : _t('newsignup.code.ask');
  }, [clicked, t]);

  // 点击发送语音验证码
  const handleSend = useCallback(() => {
    if (disable) return;
    if (validateFunc) {
      Promise.resolve(validateFunc()).then(() => {
        setVisible(true);
      });
    } else {
      setVisible(true);
    }
  }, [disable, validateFunc]);

  // 打开第一个提示弹窗
  const showFirstDialog = () => {
    if (disable) return;
    setShow(true);
    kcsensorsClick(['noCodeButton', '1']);
    kcsensorsManualTrack({
      spm: ['noCodePopUp', '1'],
      ...((trackingConfig && trackingConfig.data) || {}),
    });
  };

  // 关闭第一个弹窗
  const closeFirstDialog = () => {
    setShow(false);
  };

  const onOk = useCallback(() => {
    if (onSend) {
      Promise.resolve(onSend()).finally(() => {
        setVisible(false);
        setClicked(true);
      });
    }
  }, [onSend]);

  useEffect(() => {
    clearTimeout(timer);
    setCountTimer(0);
    if (countTime) {
      const _time = typeof countTime === 'number' ? countTime : countTime.time || 0;
      if (_time > 0) {
        startCount(_time || 60);
      }
    }
  }, [countTime, startCount]);

  return isSupportVoice ? (
    <Container>
      <BaseText onClick={showFirstDialog} disable={disable} theme={theme}>
        {text}
        {countTimer ? ` (${countTimer}s)` : ''}
      </BaseText>
      <NotReceiveDialog
        open={show}
        phone={phone}
        email={email}
        onClose={closeFirstDialog}
        isSupportVoice={isSupportVoice}
        onSend={handleSend}
      />
      <VoiceDialog
        open={visible}
        title={t('voice.title')}
        footer={
          // kufox/mui footer bug 尚未修复，故自定义来规避
          <SendBtnContainer>
            <VoiceOkBtn
              type="default"
              onClick={() => {
                setVisible(false);
              }}
            >
              {t('voice.cancel')}
            </VoiceOkBtn>
            <VoiceCancelBtn onClick={onOk} loading={loading}>
              {t('voice.send')}
            </VoiceCancelBtn>
          </SendBtnContainer>
        }
      >
        <div>
          {_phone ? (
            <Trans i18nKey="voice_tips1" ns="entrance" values={{ phone: _phone }} />
          ) : (
            t('voice.tips2')
          )}
        </div>
      </VoiceDialog>
    </Container>
  ) : (
    <PlaceElement>占位</PlaceElement>
  );
};

export default voiceCode;
